import type { RescueCase, RescueDecisionStatus } from "@io-spoke/contracts/rescue";
import type { useRescueDecision } from "../../hooks/useRescueDecision";
import { Button } from "../ui/button";

type Props = {
  rescueCase: RescueCase;
  onOpenWork: (id: string) => void | Promise<void>;
  state: ReturnType<typeof useRescueDecision>;
};
const resolvedCopy: Record<Exclude<RescueDecisionStatus, "pending" | "completed">, string> = {
  cancelled: "Decision cancelled. No recovery work was created by this decision.",
  expired: "This saved plan expired. Save a fresh plan, then review it before starting work.",
  stale: "The case changed after this plan was saved. Save a fresh plan, then review it before starting work.",
};

export function RecoveryDecisionPanel({ rescueCase, onOpenWork, state }: Props) {
  const { decision, loaded, error, pending, operation, run } = state;
  const knownWorkId = decision?.status === "completed" ? decision.entityId : rescueCase.launchedEntityId;
  return (
    <section className="mt-4 space-y-3 rounded-lg border bg-card p-4" aria-label="Recovery decision">
      {!loaded ? (
        <>
          <p role="status" className="text-sm text-muted-foreground">{pending ? "Loading your saved decision…" : "Your saved decision could not be loaded."}</p>
          {!pending ? <Button type="button" variant="outline" onClick={() => void run("load")}>Retry saved decision</Button> : null}
        </>
      ) : null}
      {loaded && decision?.status === "pending" ? (
        <>
          <p className="text-xs font-medium text-muted-foreground">Saved plan · awaiting your answer</p>
          <h4 className="break-words text-sm font-semibold">{decision.question}</h4>
          <p className="text-xs text-muted-foreground">Approval creates recovery work in IO Spoke. Nothing is sent externally.</p>
          <div className="flex flex-wrap gap-2">
            <Button type="button" className="min-h-11" loading={operation === "approve"} disabled={pending} loadingLabel="Creating recovery work…" onClick={() => void run("approve")}>Start recovery work</Button>
            <Button type="button" className="min-h-11" variant="outline" loading={operation === "cancel"} disabled={pending} loadingLabel="Cancelling decision…" onClick={() => void run("cancel")}>Not now</Button>
          </div>
        </>
      ) : null}
      {loaded && decision && decision.status !== "pending" && decision.status !== "completed" ? <p role="status" className="text-sm">{resolvedCopy[decision.status]}</p> : null}
      {loaded && knownWorkId ? (
        <>
          <p role="status" className="text-sm font-medium">Recovery work is ready.</p>
          <Button type="button" className="min-h-11" onClick={() => onOpenWork(knownWorkId)}>Open recovery work</Button>
        </>
      ) : null}
      {loaded && !knownWorkId && decision?.status !== "pending" ? (
        <Button type="button" className="min-h-11" loading={operation === "prepare"} disabled={pending} loadingLabel="Saving recovery decision…" onClick={() => void run("prepare")}>Save this plan for review</Button>
      ) : null}
      {error ? <p role="alert" className="break-words text-sm text-destructive">{error}</p> : null}
    </section>
  );
}
